import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Select, { SingleValue } from 'react-select';
import { MenuComponent } from '../../../../../../_metronic/assets/ts/components';
import { KTSVG } from '../../../../../../_metronic/helpers';
import { useQueryRequest } from '../../core/QueryRequestProvider';
import { getPartys, getCities } from '../../core/_requests';

interface Party {
  id: string;
  party_name: string;
  city_name: string;
  party_code: string;
  label?: string;
  value?: string;
}

const PartysListFilter: React.FC = () => {
  const { state, updateState } = useQueryRequest();
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [partyList, setPartyList] = useState<Party[]>([]);
  const [cityList, setCityList] = useState<{ label: string; value: string }[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const partyCodeInputRef = useRef<HTMLInputElement>(null);
  const citySelectRef = useRef<any>(null);
  const applyButtonRef = useRef<HTMLButtonElement>(null);
  const partyNameRef = useRef<any>(null);

  const [reqData, setReqData] = useState({
    party_code: '',
    party_name: '',
    city: '',
  });

  const [selectedCity, setSelectedCity] = useState<{ label: string; value: string } | null>(null);

  const fetchPartyList = async () => {
    try {
      const result = await getPartys('');
      if (result && result.data && Array.isArray(result.data.data)) {
        const selectedCityName = cityList.find((city) => city.value === reqData.city)?.label || '';
        const filteredList = result.data.data.filter(
          (party: Party) =>
            party.city_name.includes(selectedCityName) &&
            party.party_name.includes(reqData.party_name) &&
            party.party_code.includes(reqData.party_code)
        );
        setPartyList(filteredList);
      } else {
        console.warn('Invalid API response format:', result);
        setPartyList([]);
      }
    } catch (err: any) {
      console.error('Something went wrong:', err.message || 'Unknown error');
    }
  };

  const resetData = () => {
    updateState({ filter: undefined, page: 1, perPage: state.perPage });
    setReqData({
      party_code: '',
      party_name: '',
      city: '',
    });
    setSelectedCity(null);
    partyCodeInputRef.current?.focus();

    fetchPartyList();
  };

  const filterData = () => {
    const selectedCityName = selectedCity?.label || '';
    const filterPayload: any = {};
    if (reqData.party_code) filterPayload['filter[partycode]'] = reqData.party_code;
    if (reqData.party_name) filterPayload['filter[partyname]'] = reqData.party_name;
    if (selectedCityName) filterPayload['filter[cityname]'] = selectedCityName;

    updateState({
      filter: filterPayload,
      page: 1,
      perPage: state.perPage,
    });
    fetchPartyList();
    setIsModelOpen(false);
  };

  const hasValuesToReset = () => {
    return reqData.party_code || reqData.party_name || selectedCity;
  };

  const handleClose = () => {
    resetData();
    setIsModelOpen(false);
  };

  useEffect(() => {
    if (isModelOpen) {
      getCities()
        .then((data) => {
          if (Array.isArray(data.data)) {
            const cityOptions = data.data.map((city: any) => ({
              label: city.city_name,
              value: city.id.toString(),
            }));
            setCityList(cityOptions);
          } else {
            console.warn('Unexpected API Response:', data);
            setCityList([]);
          }
        })
        .catch((error) => console.error('Error fetching cities:', error));
    }
  }, [isModelOpen]);

  useEffect(() => {
    MenuComponent.reinitialization();
    fetchPartyList();
  }, []);

  useEffect(() => {
    if (isModelOpen && partyCodeInputRef.current) {
      setTimeout(() => {
        partyCodeInputRef.current?.focus();
      }, 100);
    }
  }, [isModelOpen]);

  const handleCityFocus = () => {
    if (citySelectRef.current) {
      citySelectRef.current.focus();
      citySelectRef.current.openMenu();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      !(event.target as HTMLElement).closest('.react-select__menu')
    ) {
      setIsModelOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCityChange = (selectedOption: SingleValue<{ label: string; value: string }>) => {
    setSelectedCity(selectedOption);
    setReqData({ ...reqData, city: selectedOption ? selectedOption.value : '' });
    setTimeout(() => {
      applyButtonRef.current?.focus(); // Move focus to Apply button after selection
    }, 100);
  };
  
  

  // Handle Tab key to prevent auto-selection
  const handleCityKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (!selectedCity) {
        e.preventDefault(); // Prevent default Tab behavior
        setSelectedCity(null); // Ensure no city is selected
        setReqData({ ...reqData, city: '' }); // Clear city in reqData
        applyButtonRef.current?.focus(); // Move focus to Apply button
      }
    } else if (e.key === 'Enter') {
      // When Enter key is pressed, select the current option if available
      if (citySelectRef.current?.state.isOpen && citySelectRef.current.state.selectedOption) {
        const selectedOption = citySelectRef.current.state.selectedOption;
        setSelectedCity(selectedOption);
        setReqData({ ...reqData, city: selectedOption.value });
        setTimeout(() => {
          applyButtonRef.current?.focus(); // Move focus to Apply button after selection
        }, 100);
      }
    }
  };
  

  const handleApplyKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      filterData();
    }
  };

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: '#E4E6EF',
      borderWidth: '2px',
      minHeight: 'calc(1.5em + .75rem + 16px)',
      borderRadius: '0.475rem',
      cursor: 'pointer',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#cdcdde',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      lineHeight: '1.5',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: 'calc(1.5em + .75rem + 16px)',
    }),
    option: (provided: any) => ({
      ...provided,
      cursor: 'pointer',
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-light-primary me-3"
        onClick={() => setIsModelOpen(!isModelOpen)}
      >
        <KTSVG path="/media/icons/duotune/general/gen031.svg" className="svg-icon-2" />
        Filter
      </button>
      <div
        ref={dropdownRef}
        className={`menu menu-sub menu-sub-dropdown mw-650px w-md-700px end-0 top-0 position-absolute ${
          isModelOpen ? 'd-block' : 'd-none'
        }`}
        data-kt-menu="true"
      >
        <div className="px-7 py-5">
          <div className="fs-5 text-dark d-flex align-items-center justify-content-between fw-bolder">
            Filter Option
            <button
              type="button"
              className="btn btn-light btn-active-light-primary fw-bold px-6"
              onClick={handleClose}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>
        <div className="separator border-gray-200"></div>
        <div className="px-7 scroll-y py-5" style={{ maxHeight: '400px' }}>
          <Row className="mb-5">
            <Col lg={6}>
              <label className="fw-bold fs-6 mb-2">Party Code</label>
              <input
                ref={partyCodeInputRef}
                type="text"
                maxLength={50}
                className="form-control"
                placeholder="Enter party code"
                value={reqData.party_code}
                onChange={(e) => setReqData({ ...reqData, party_code: e.target.value.trimStart() })}
                tabIndex={1}
                onKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent form submission or unwanted behavior
                      partyNameRef.current?.focus(); // Move focus to senderVatav field
                    }
                  
                }}
              />
            </Col>
            <Col lg={6}>
              <label className="fw-bold fs-6 mb-2">Party Name</label>
              <input
                type="text"
                maxLength={50}
                className="form-control"
                ref={partyNameRef}
                placeholder="Enter party name"
                value={reqData.party_name}
                onChange={(e) => setReqData({ ...reqData, party_name: e.target.value.trimStart() })}
                tabIndex={2}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unwanted behavior
                    citySelectRef.current?.focus(); // Move focus to senderVatav field
                  }
                
              }}
              />
            </Col>
          </Row>
          <Row className="mb-5">
            <Col xl={6}>
              <label className="fw-bold fs-6 mb-2">City</label>
              <Select
                ref={citySelectRef}
                options={cityList}
                placeholder="Select City"
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                value={selectedCity}
                onChange={handleCityChange}
                onFocus={handleCityFocus}
                onKeyDown={handleCityKeyDown} // Add keydown handler
                // isClearable // Allow clearing the selection
                tabIndex={3}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <button
              ref={applyButtonRef}
              type="button"
              className="btn sbmt-btn btn-primary px-6 me-3"
              onClick={filterData}
              onKeyDown={handleApplyKeyDown}
              tabIndex={4}
            >
              Apply
            </button>
            {hasValuesToReset() && (
              <button
                type="button"
                className="btn btn-light btn-active-light-primary fw-bold px-6"
                onClick={resetData}
                tabIndex={5}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export { PartysListFilter };